// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { Band, Complex, ComplexCode, DSNSnapshot, Dish, Signal, Target } from '../types';
import { COMPLEX_META } from '../data/complexes';

const COMPLEX_NAME: Record<ComplexCode, string> = {
  gdscc: 'Goldstone',
  mdscc: 'Madrid',
  cdscc: 'Canberra',
};

function parseFloatOrNull(value: string | null): number | null {
  if (value === null || value === '') return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function parseIntOrDefault(value: string | null, fallback: number): number {
  if (value === null || value === '') return fallback;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseBool(value: string | null): boolean {
  return value === 'true';
}

function parseBand(value: string | null): Band {
  if (!value) return '';
  const v = value as Band;
  if (['S', 'X', 'K', 'Ka', 'Ku', 'L', 'C'].includes(v)) return v;
  return '';
}

function parseSignal(el: Element, direction: 'up' | 'down'): Signal {
  return {
    direction,
    active: parseBool(el.getAttribute('active')),
    signalType: el.getAttribute('signalType') ?? 'none',
    dataRate: parseIntOrDefault(el.getAttribute('dataRate'), 0),
    frequency: parseIntOrDefault(el.getAttribute('frequency'), 0),
    band: parseBand(el.getAttribute('band')),
    power: Number.parseFloat(el.getAttribute('power') ?? '0') || 0,
    spacecraft: el.getAttribute('spacecraft') ?? '',
    spacecraftId: parseIntOrDefault(el.getAttribute('spacecraftID'), 0),
  };
}

function parseTarget(el: Element): Target {
  return {
    name: el.getAttribute('name') ?? '',
    id: parseIntOrDefault(el.getAttribute('id'), 0),
    uplegRange: Number.parseFloat(el.getAttribute('uplegRange') ?? '-1'),
    downlegRange: Number.parseFloat(el.getAttribute('downlegRange') ?? '-1'),
    rtlt: Number.parseFloat(el.getAttribute('rtlt') ?? '-1'),
  };
}

function parseDish(el: Element, complex: ComplexCode): Dish {
  const signals: Signal[] = [];
  el.querySelectorAll('upSignal').forEach((s) => signals.push(parseSignal(s, 'up')));
  el.querySelectorAll('downSignal').forEach((s) => signals.push(parseSignal(s, 'down')));
  const targets: Target[] = [];
  el.querySelectorAll('target').forEach((t) => targets.push(parseTarget(t)));
  return {
    name: el.getAttribute('name') ?? '',
    azimuthAngle: parseFloatOrNull(el.getAttribute('azimuthAngle')),
    elevationAngle: parseFloatOrNull(el.getAttribute('elevationAngle')),
    windSpeed: parseFloatOrNull(el.getAttribute('windSpeed')),
    isMSPA: parseBool(el.getAttribute('isMSPA')),
    isArray: parseBool(el.getAttribute('isArray')),
    isDDOR: parseBool(el.getAttribute('isDDOR')),
    activity: el.getAttribute('activity') ?? 'Unknown',
    signals,
    targets,
    complex,
  };
}

export function parseDSNXml(xmlText: string): DSNSnapshot {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');
  const root = doc.querySelector('dsn');
  if (!root) throw new Error('Invalid DSN XML: no <dsn> root');

  const complexes: Complex[] = [];
  let currentComplex: Complex | null = null;

  for (const child of Array.from(root.children)) {
    if (child.tagName === 'station') {
      const code = (child.getAttribute('name') ?? '') as ComplexCode;
      const meta = COMPLEX_META[code];
      currentComplex = {
        code,
        friendlyName: child.getAttribute('friendlyName') ?? COMPLEX_NAME[code] ?? code,
        timeUTC: parseIntOrDefault(child.getAttribute('timeUTC'), Date.now()),
        timeZoneOffset: Number.parseFloat(child.getAttribute('timeZoneOffset') ?? '0'),
        dishes: [],
        lat: meta?.lat ?? 0,
        lon: meta?.lon ?? 0,
        country: meta?.country ?? '',
      };
      complexes.push(currentComplex);
    } else if (child.tagName === 'dish' && currentComplex) {
      currentComplex.dishes.push(parseDish(child, currentComplex.code));
    }
  }

  const timestampEl = root.querySelector('timestamp');
  const timestampUTC = timestampEl ? parseIntOrDefault(timestampEl.textContent, Date.now()) : Date.now();

  return { timestampUTC, complexes };
}
